import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { trackClickEvent } from "../utils/analytics";
import sideMenuVideo from "../assets/images/sidemenuvideo2.mp4";

export default function SideMenuOverlay({ isOpen, onClose }) {
  const [servicesOpen, setServicesOpen] = useState(false);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  const showServices = () => {
    if (window.innerWidth >= 768) {
      clearTimeout(timeoutRef.current);
      setServicesOpen(true);
    }
  };

  const hideServices = () => {
    if (window.innerWidth >= 768) {
      timeoutRef.current = setTimeout(() => setServicesOpen(false), 100);
    }
  };

  const handleServicesClick = (e) => {
    if (window.innerWidth < 768) {
      e.preventDefault();
      setServicesOpen((prev) => !prev);
    } else {
      onClose();
      trackClickEvent("SideMenu - Services");
      navigate("/services");
    }
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
  document.body.style.overflow = isOpen ? "hidden" : "";

  return () => {
    document.body.style.overflow = "";
  };
}, [isOpen]);

  return (
    <div
      className={`fixed inset-0 z-40 transition-opacity duration-300 ease-in-out flex ${
        isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Dim Background */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Main Layout */}
      <div className="relative z-40 flex h-full w-full">
        {/* Left Glass Panel */}
        <div className="absolute inset-0 md:top-6 md:left-4 md:bottom-6 md:w-[32%] bg-white/10 backdrop-blur-lg text-white px-4 pt-10 pb-10 flex flex-col z-40 md:rounded-lg md:shadow-lg drop-shadow-sm overflow-y-auto">
          <div className="flex flex-col md:justify-between h-full space-y-6">
            <ul className="space-y-2 text-7xl font-heading font-medium tracking-normal leading-none"
              style={{ WebkitFontSmoothing: "antialiased", textRendering: "optimizeLegibility" }}
            >
              {/* ABOUT */}
              <li className="relative group">
                <Link
                  to="/about"
                  onClick={() => {
                    onClose();
                    trackClickEvent("SideMenu - About");
                  }}
                  className="inline-block relative pb-1 drop-shadow-sm"
                >
                  ABOUT
                  <span className="absolute left-0 bottom-[1px] h-px w-full bg-white/30" />
                  <span className="absolute left-0 bottom-[2px] h-px w-0 bg-white transition-all duration-500 group-hover:w-full" />
                </Link>
              </li>

              {/* SERVICES */}
              <li
                onMouseEnter={showServices}
                onMouseLeave={hideServices}
                className="relative group"
              >
                <div className="flex items-center gap-2">
                  <Link
                    to="/services"
                    onClick={handleServicesClick}
                    className="inline-block relative pb-1 block drop-shadow-sm"
                  >
                    SERVICES
                    <span className="absolute left-0 bottom-[1px] h-px w-full bg-white/30" />
                    <span className="absolute left-0 bottom-[2px] h-px w-0 bg-white transition-all duration-500 group-hover:w-full" />
                  </Link>
                </div>

               {/* 
               {servicesOpen && (
                  <ul className="ml-4 mt-3 space-y-2 text-2xl font-medium text-white/80">
                    <li>
                      <Link
                        to="/services#aviation"
                        onClick={() => {
                          onClose();
                          trackClickEvent("SideMenu - Aviation");
                        }}
                      >
                        AVIATION
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/services#media"
                        onClick={() => {
                          onClose();
                          trackClickEvent("SideMenu - Media");
                        }}
                      >
                        MEDIA
                      </Link>
                    </li>
                  </ul>
                )}
                */}
              </li> 
            

              {/* MEDIA */}
              <li className="relative group">
                <Link
                  to="/media"
                  onClick={() => {
                    onClose();
                    trackClickEvent("SideMenu - Media Page");
                  }}
                  className="inline-block relative pb-1 block drop-shadow-sm"
                >
                  PORTFOLIO
                  <span className="absolute left-0 bottom-[1px] h-px w-full bg-white/30" />
                  <span className="absolute left-0 bottom-[2px] h-px w-0 bg-white transition-all duration-500 group-hover:w-full" />
                </Link>
              </li>

              {/* CONTACT */}
              <li className="relative group">
                <Link
                  to="/contact"
                  onClick={() => {
                    onClose();
                    trackClickEvent("SideMenu - Contact");
                  }}
                  className="inline-block relative pb-1 block drop-shadow-sm"
                >
                  CONTACT
                  <span className="absolute left-0 bottom-[1px] h-px w-full bg-white/30" />
                  <span className="absolute left-0 bottom-[2px] h-px w-0 bg-white transition-all duration-500 group-hover:w-full" />
                </Link>
              </li>
            </ul>

            {/* LOGIN */}
            <div className="border-t border-white/20 pt-6 text-lg text-white flex items-center gap-3 font-medium">
              <User className="w-5 h-5" />
              <Link
                to="/login"
                onClick={() => {
                  onClose();
                  trackClickEvent("SideMenu - Login");
                }}
                className="hover:text-white transition"
              >
                LOGIN
              </Link>
            </div>
          </div>
        </div>

        {/* Background Video */}
        <div className="absolute inset-0 z-30">
          <video
            src={sideMenuVideo}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-90"
          />
        </div>
      </div>
    </div>
  );
}
